import { computed, observable, action, makeObservable } from 'mobx'
import { task } from 'mobx-task'
import ModalViewModel, {
  ModalViewModelClosingSignal,
} from 'infra/ModalViewModel'
import { validationContext, required } from 'validx'
import { CursorPageQuery, CursorPaged, Nillable } from 'types/definitions'
import { maxLength } from 'utils/validation-utils'
import { toStream } from 'utils/mobx-util'
import { AlertState } from 'alerts/Alert'
import Workspace from 'iam/models/Workspace'
import Member from 'iam/models/Member'
import { TransactionType } from 'payouts/TransactionType'
import { UserTransactionReason } from 'payouts/UserTransactionReason'
import { WorkspaceQueryPort, MemberQueryPort, AlertPort } from '../ports'
import { PayoutReviewStore } from '../PayoutReviewStore'
import { ConfirmUserTransactionViewModel } from './ConfirmUserTransactionViewModel'
import { CancellationToken } from 'infra/cancellation'

/**
 * Modal for creating a new user transaction.
 */
export class NewUserTransactionViewModel extends ModalViewModel {
  private readonly AMOUNT_REQUIRING_CONFIRMATION = 500 as const
  private readonly MAX_MEMO_LENGTH = 280 as const
  private readonly MAX_AMOUNT = 999_999_999 as const // ¯\_(ツ)_/¯

  /**
   * The selected workspace.
   */
  @observable selectedWorkspace: Workspace | null = null

  /**
   * The selected member.
   */
  @observable selectedMember: Member | null = null

  /**
   * The transaction memo. Optional.
   */
  @observable memo = ''

  /**
   * The transaction amount.
   */
  @observable amount = 0

  /**
   * The transaction type.
   */
  @observable transactionType = TransactionType.Payment

  /**
   * The transaction reason.
   */
  @observable reason: UserTransactionReason | null = null

  /**
   * Used to request confirmation for large transaction amounts.
   */
  readonly confirmationVM = new ConfirmUserTransactionViewModel(
    this,
    this.showing$,
  )

  /**
   * Validation.
   */
  readonly validation = validationContext(this, {
    selectedMember: [required('Recipient is required')],
    selectedWorkspace: [required('Workspace is required')],
    memo: [
      maxLength(
        this.MAX_MEMO_LENGTH,
        `The memo can be at most ${this.MAX_MEMO_LENGTH}`,
      ),
    ],
    reason: [required('Reason is required')],
    amount: [
      ({ value }) => {
        if (value <= 0) {
          return 'Please enter a positive amount greater than zero'
        }

        if (value > this.MAX_AMOUNT) {
          return `Woah there big money baller! Try a smaller amount`
        }

        return true
      },
    ],
  })

  /**
   * Constructor
   */
  constructor(
    private payoutReviewStore: PayoutReviewStore,
    private adapter: WorkspaceQueryPort & MemberQueryPort & AlertPort,
    closingSignal?: ModalViewModelClosingSignal,
  ) {
    super(closingSignal)
    makeObservable(this)
    this.searchWorkspaces = this.searchWorkspaces.bind(this)
    this.searchMembers = this.searchMembers.bind(this)

    // Reset inputs on show.
    this.show$.subscribe(this.reset)

    // Deselect the recipient when the workspace selection changes.
    toStream(() => this.selectedWorkspace, false)
      .pipe(this.whileShowing())
      .subscribe(() => this.selectMember(null))

    // Deselect the reason when the type changes
    toStream(() => this.transactionType, false)
      .pipe(this.whileShowing())
      .subscribe(() => this.selectReason(null))

    // Clear field validations on change
    this._clearValidationOnChange('amount')
    this._clearValidationOnChange('selectedWorkspace')
    this._clearValidationOnChange('selectedMember')
    this._clearValidationOnChange('reason')
    this._clearValidationOnChange('memo')
  }

  /**
   * Whether the editor is disabled.
   */
  @computed
  get disabled() {
    return this.createTransaction.pending
  }

  /**
   * The possible reasons to choose from.
   */
  @computed.struct
  get availableReasons() {
    switch (this.transactionType) {
      case TransactionType.Payment:
        return [UserTransactionReason.Bonus, UserTransactionReason.MiscPayment]
      case TransactionType.Abatement:
        return [
          UserTransactionReason.BackgroundCheck,
          UserTransactionReason.Penalty,
          UserTransactionReason.MiscAbatement,
        ]
    }
    return []
  }

  /**
   * Reset the inputs.
   */
  @action.bound
  reset() {
    this.selectedWorkspace = null
    this.selectedMember = null
    this.amount = 0
    this.transactionType = TransactionType.Payment
    this.reason = null
    this.memo = ''
    this.validation.reset()
  }

  /**
   * Selects the workspace.
   */
  @action.bound
  selectWorkspace(workspace: Nillable<Workspace>) {
    this.selectedWorkspace = workspace ?? null
  }

  /**
   * Selects the member.
   */
  @action.bound
  selectMember(member: Nillable<Member>) {
    this.selectedMember = member ?? null
  }

  /**
   * Selects the transaction type.
   */
  @action.bound
  selectType(type: TransactionType) {
    this.transactionType = type
  }

  /**
   * Selects the transaction type.
   */
  @action.bound
  selectReason(reason: Nillable<UserTransactionReason>) {
    this.reason = reason ?? null
  }

  /**
   * Searches for workspaces.
   */
  searchWorkspaces(searchText: string, pagination: CursorPageQuery) {
    return this.adapter.listPlatformWorkspaces({
      searchText,
      pagination,
    })
  }

  /**
   * Searches for members.
   */
  async searchMembers(
    searchText: string,
    pagination: CursorPageQuery,
    ct: CancellationToken,
  ) {
    if (!this.selectedWorkspace) {
      return CursorPaged.empty as CursorPaged<Member>
    }

    return this.adapter.listWorkspaceMembers(
      {
        searchText,
        pagination,
        workspaceId: this.selectedWorkspace.id,
      },
      ct,
    )
  }

  /**
   * Creates the transaction.
   */
  createTransaction = task.resolved(async () => {
    this.validation.reset().validate()

    if (!this.validation.isValid) {
      return
    }

    if (!this.selectedMember || !this.reason) {
      return
    }

    if (this.amount >= this.AMOUNT_REQUIRING_CONFIRMATION) {
      const confirmed = await this.confirmationVM.requestConfirmation()
      if (!confirmed) {
        return
      }
    }

    await this.payoutReviewStore.createUserTransaction({
      type: this.transactionType,
      reason: this.reason,
      memo: this.memo,
      amount: this.amount,
      recipient: this.selectedMember,
    })
    this.adapter
      .newAlert()
      .withState(AlertState.Success)
      .saying('Success!', 'The transaction has been entered into the system.')
      .autoDismiss()
      .show()
    this.close()
  })

  /**
   * Sets up automatic clearing of validation errors when the field changes.
   * @param field
   * @private
   */
  private _clearValidationOnChange(field: keyof this) {
    toStream(() => this[field])
      .pipe(this.whileShowing())
      .subscribe(() => this.validation.clearErrors(field as string))
  }
}
